import React, { useState, useEffect } from 'react';
import { JsonRpcProvider } from 'ethers';

const INFURA_KEY = process.env.REACT_APP_INFURA_API_KEY;
const PROVIDER_URL = INFURA_KEY
  ? `https://mainnet.infura.io/v3/${INFURA_KEY}`
  : 'https://ethereum.publicnode.com';

const TEXT_KEYS = [
  'description',
  'url',
  'com.twitter',
  'com.github',
  'org.telegram',
  'com.discord',
  'location',
  'email'
];

const UserENSRecords = ({ ensName }) => {
  console.log('📋 UserENSRecords rendered with ensName:', ensName);
  console.log('🌐 Using provider URL:', PROVIDER_URL);

  const [records, setRecords] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    console.log('🔍 UserENSRecords useEffect triggered with ensName:', ensName);
    if (!ensName) {
      setLoading(false);
      return;
    }

    const fetchRecords = async () => {
      console.log('⏳ Starting fetchRecords for', ensName);
      try {
        console.log('🔄 Creating provider...');
        const provider = new JsonRpcProvider(PROVIDER_URL);
        console.log('✅ Provider created, now calling getResolver...');
        const resolver = await provider.getResolver(ensName);
        console.log('🔍 getResolver returned:', resolver ? 'Resolver found' : 'Resolver null');
        if (!resolver) {
          console.log('⚠️ No resolver found for', ensName);
          setRecords({});
          setLoading(false);
          return;
        }

        console.log('🔍 Fetching text records...');
        const recordPromises = TEXT_KEYS.map(async (key) => {
          try {
            const value = await resolver.getText(key);
            return { key, value: value || '' };
          } catch (err) {
            console.log(`⚠️ Error fetching key ${key}:`, err.message);
            return { key, value: '' };
          }
        });

        const results = await Promise.all(recordPromises);
        console.log('✅ All record promises resolved:', results);
        const fetched = {};
        results.forEach(({ key, value }) => {
          if (value) fetched[key] = value;
        });
        setRecords(fetched);
      } catch (err) {
        console.error('❌ Error in fetchRecords:', err);
        setError('Could not load profile records');
      } finally {
        setLoading(false);
      }
    };

    fetchRecords();
  }, [ensName]);

  if (!ensName) return null;
  if (loading) return <div className="ens-records-loading">Loading profile records…</div>;
  if (error) return <div className="ens-records-error">{error}</div>;

  const hasRecords = Object.keys(records).length > 0;
  if (!hasRecords) return null;

  return (
    <div className="ens-profile-records" style={{ marginTop: '2rem' }}>
      <h3 style={{ color: 'var(--gold)', marginBottom: '1rem' }}>ENS Profile</h3>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: '1rem',
        background: 'var(--card-bg)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '1.5rem'
      }}>
        {records.description && (
          <div style={{ gridColumn: '1 / -1', marginBottom: '0.5rem' }}>
            <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>Bio</div>
            <div style={{ color: 'var(--text-light)', lineHeight: '1.5' }}>{records.description}</div>
          </div>
        )}

        {records.url && (
          <RecordItem
            icon="🌐"
            label="Website"
            value={records.url}
            href={records.url.startsWith('http') ? records.url : `https://${records.url}`}
          />
        )}
        {records['com.twitter'] && (
          <RecordItem
            icon="𝕏"
            label="X (Twitter)"
            value={`@${records['com.twitter']}`}
            href={`https://x.com/${records['com.twitter']}`}
          />
        )}
        {records['com.github'] && (
          <RecordItem
            icon="🐙"
            label="GitHub"
            value={`@${records['com.github']}`}
            href={`https://github.com/${records['com.github']}`}
          />
        )}
        {records['org.telegram'] && (
          <RecordItem
            icon="📱"
            label="Telegram"
            value={`@${records['org.telegram']}`}
            href={`https://t.me/${records['org.telegram']}`}
          />
        )}
        {records['com.discord'] && (
          <RecordItem
            icon="💬"
            label="Discord"
            value={records['com.discord']}
          />
        )}
        {records.location && (
          <RecordItem
            icon="📍"
            label="Location"
            value={records.location}
          />
        )}
        {records.email && (
          <RecordItem
            icon="✉️"
            label="Email"
            value={records.email}
            href={`mailto:${records.email}`}
          />
        )}
      </div>
    </div>
  );
};

const RecordItem = ({ icon, label, value, href }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
    <div style={{ fontSize: '1.2rem', minWidth: '28px' }}>{icon}</div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>{label}</div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'var(--gold)',
            textDecoration: 'none',
            wordBreak: 'break-all',
            fontSize: '0.95rem'
          }}
        >
          {value}
        </a>
      ) : (
        <div style={{ color: 'var(--text-light)', wordBreak: 'break-all', fontSize: '0.95rem' }}>{value}</div>
      )}
    </div>
  </div>
);

export default UserENSRecords;
